import { ROUTES } from './routes';
import { materialGlyph, type GlyphRef } from '../theme/glyph';
import { SETTINGS_TABS, type SettingsTabMeta, type SettingsFieldMeta } from './settingsTabs';
import { NAV_DESTINATIONS, type NavDestination } from './navDestinations';
import { HELP_LINKS } from './helpLinks';

/**
 * Client-side command registry — the instant, zero-latency half of the Cmd-K
 * palette. Navigation destinations ("Go to") and settings pages are matched
 * synchronously on every keystroke (no debounce, no network) so the palette
 * paints useful results before the server hybrid-search round-trip returns.
 *
 * ACL posture (see `.claude/rules/entity-acl.md`): an entry is only ever
 * surfaced when `visible(ctx)` is true. Visibility predicates mirror the exact
 * gates the rest of the app already enforces:
 *   - "Go to" Trash / Audit  → hidden for read-only roles, GUEST/VIEWER (the shared `useIsReadOnly` rule)
 *   - "Go to" Workspace visualizer → `useCanUseFeature('knowledgeGraph')`
 *   - Settings entries        → derived from the `settingsTabs` SSOT, so Billing
 *     (admin/owner) and Connectors (non-PHI) inherit that single source
 * An invisible entry never renders and never scores — it cannot be surfaced by
 * any query.
 */

export type CommandGroup = 'goto' | 'settings' | 'help' | 'action';

export interface CommandContext {
  /** Active workspace doc — forwarded to the settings SSOT predicates. */
  readonly workspace: unknown;
  readonly isPhiWorkspace: boolean;
  /** Read-only role — hides Trash / Audit, matching the sidebar. */
  readonly isReadOnly: boolean;
  /** Knowledge-graph feature entitlement (`useCanUseFeature`). */
  readonly graphEnabled: boolean;
  readonly navigate: (to: string) => void;
}

export interface CommandEntry {
  readonly id: string;
  readonly label: string;
  /**
   * Optional second line. Set only by live help-centre results, where the
   * matching snippet is what distinguishes two same-titled section hits;
   * the fixed registry entries below are self-describing and omit it.
   */
  readonly description?: string;
  readonly keywords: readonly string[];
  readonly glyph: GlyphRef;
  readonly group: CommandGroup;
  readonly visible: (ctx: CommandContext) => boolean;
  readonly perform: (ctx: CommandContext) => void;
}

export interface ScoredCommand {
  readonly entry: CommandEntry;
  readonly score: number;
}

/**
 * "Go to" entries are DERIVED from the `NAV_DESTINATIONS` SSOT — add a page there
 * once and it becomes searchable here, guarded by the nav drift-guard test.
 * `NavVisibilityContext` (isReadOnly, graphEnabled) is a subset of
 * `CommandContext`, so a destination's `visible` runs directly against `ctx`.
 */
function navEntry(dest: NavDestination): CommandEntry {
  return {
    id: dest.id,
    label: dest.label,
    keywords: dest.keywords,
    glyph: dest.glyph,

    group: 'goto',
    visible: (ctx) => dest.visible(ctx),
    perform: (ctx) => ctx.navigate(dest.route),
  };
}

const GOTO_ENTRIES: ReadonlyArray<CommandEntry> = NAV_DESTINATIONS.map(navEntry);

/** `settings?tab=<slug>` — the one URL both tab and field entries navigate to. */
function settingsTabHref(slug: SettingsTabMeta['slug']): string {
  return `${ROUTES.settings}?tab=${slug}`;
}

/** Whether the tab (and therefore its fields) is visible in the current context. */
function tabVisible(tab: SettingsTabMeta, ctx: CommandContext): boolean {
  return tab.visible({ workspace: ctx.workspace, isPhiWorkspace: ctx.isPhiWorkspace });
}

function settingsEntry(tab: SettingsTabMeta): CommandEntry {
  return {
    id: `settings-${tab.slug}`,
    label: `${tab.label} settings`,
    // Synonyms are co-located on the tab in the settingsTabs SSOT; add the label
    // + "settings" so both the exact name and "X settings" match.
    keywords: [tab.label.toLowerCase(), 'settings', ...tab.keywords],
    glyph: tab.glyph,

    group: 'settings',
    // Reuse the SSOT predicate — Billing (admin/owner) and Connectors (non-PHI)
    // stay in lockstep with the settings page, no forked logic.
    visible: (ctx) => tabVisible(tab, ctx),
    perform: (ctx) => ctx.navigate(settingsTabHref(tab.slug)),
  };
}

/** kebab-case a field label for a stable, readable entry id. */
function fieldSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * A field entry ("ORCID iD", "Theme", "Delete account") — DERIVED from the tab's
 * `fields` in the settingsTabs SSOT. It lands the user on the parent tab and
 * inherits the tab's exact visibility, so a hidden tab never leaks its fields.
 */
function fieldEntry(tab: SettingsTabMeta, field: SettingsFieldMeta): CommandEntry {
  return {
    id: `settings-field-${tab.slug}-${fieldSlug(field.label)}`,
    label: field.label,
    keywords: field.keywords ?? [],
    glyph: tab.glyph,

    group: 'settings',
    visible: (ctx) => tabVisible(tab, ctx),
    perform: (ctx) => ctx.navigate(settingsTabHref(tab.slug)),
  };
}

/**
 * A settings tab's entries: the tab itself, then one per declared field, in
 * declaration order. Exported so tests can exercise the REAL derivation against
 * a synthetic tab (e.g. proving a field on a hideable tab inherits its hidden
 * state) instead of re-implementing it.
 */
export function buildTabEntries(tab: SettingsTabMeta): ReadonlyArray<CommandEntry> {
  return [settingsEntry(tab), ...tab.fields.map((field) => fieldEntry(tab, field))];
}

const SETTINGS_ENTRIES: ReadonlyArray<CommandEntry> = SETTINGS_TABS.flatMap(buildTabEntries);

/**
 * Help-centre entries.
 *
 * Backed by the SAME curated `HELP_LINKS` map every in-product "Learn more"
 * already uses, so the palette can never point somewhere the rest of the app
 * doesn't. Labels + synonyms live here because that map is URLs only.
 *
 * These are the FALLBACK rows, not the primary corpus. The palette's Help group
 * searches the real help-centre index (`hooks/data/useHelpSearch`); these dozen
 * pages render while that index is still building, or if it could not be built.
 * They stay curated on purpose — they are the pages the product itself links to
 * from settings rows and "Learn more" affordances.
 *
 * Opens in a new tab: the help centre is a different site, and swapping the
 * app out from under someone mid-task is not what a search result should do.
 */
const HELP_ENTRIES: ReadonlyArray<CommandEntry> = (
  [
    [
      'help-home',
      'Help center',
      HELP_LINKS.HOME,
      ['docs', 'documentation', 'support', 'guide', 'centre'],
    ],
    [
      'help-keyboard-shortcuts',
      'Keyboard shortcuts',
      HELP_LINKS.KEYBOARD_SHORTCUTS,
      ['shortcut', 'hotkey', 'keyboard', 'cmd', 'ctrl', 'save', 'print'],
    ],
    [
      'help-sharing',
      'Sharing notes and collections',
      HELP_LINKS.SHARING,
      ['share', 'permissions', 'access', 'private'],
    ],
    [
      'help-protocols',
      'Protocol library',
      HELP_LINKS.PROTOCOLS,
      ['protocol', 'procedure', 'sop', 'method'],
    ],
    [
      'help-artifacts',
      "What's in your workspace",
      HELP_LINKS.ARTIFACTS,
      ['note', 'attachment', 'file', 'upload', 'collection', 'equipment', 'link'],
    ],
    [
      'help-capture-stream',
      'Protocol runs',
      HELP_LINKS.PROTOCOL_RUNS,
      ['capture', 'capture stream', 'feed', 'timeline'],
    ],
    [
      'help-connectors',
      'Connectors',
      HELP_LINKS.CONNECTORS,
      ['integration', 'notion', 'slack', 'drive'],
    ],
    [
      'help-ai-app-access',
      'AI app access (MCP)',
      HELP_LINKS.AI_APP_ACCESS,
      ['mcp', 'claude', 'chatgpt', 'cursor'],
    ],
    [
      'help-preferences',
      'Personal preferences',
      HELP_LINKS.PERSONAL_PREFERENCES,
      ['memory', 'personalisation', 'preferences'],
    ],
    [
      'help-invite',
      'Inviting team members',
      HELP_LINKS.INVITE_TEAM,
      ['invite', 'team', 'seat', 'member', 'role'],
    ],
    [
      'help-account',
      'Managing your account',
      HELP_LINKS.MANAGE_ACCOUNT,
      ['account', 'ownership', 'transfer', 'billing'],
    ],
    ['help-hipaa', 'HIPAA compliance', HELP_LINKS.HIPAA, ['hipaa', 'phi', 'compliance', 'baa']],
    [
      'help-cookies',
      'Cookies and privacy',
      HELP_LINKS.COOKIES,
      ['cookie', 'privacy', 'consent', 'gdpr'],
    ],
  ] as const
).map(([id, label, href, keywords]) => ({
  id,
  label,
  keywords: [...keywords],
  glyph: materialGlyph('menu_book'),
  group: 'help' as const,
  visible: () => true,
  perform: () => window.open(href, '_blank', 'noopener,noreferrer'),
}));

/** All command entries in declaration order (Go to, Settings, then Help). */
export const COMMAND_ENTRIES: ReadonlyArray<CommandEntry> = [
  ...GOTO_ENTRIES,
  ...SETTINGS_ENTRIES,
  ...HELP_ENTRIES,
];

// Precompute a lowercased match surface per entry once, at module load — the
// scorer runs on every keystroke and must not re-allocate these.
interface MatchSurface {
  readonly label: string;
  readonly words: readonly string[];
  readonly keywords: readonly string[];
}
const SURFACES = new Map<string, MatchSurface>(
  COMMAND_ENTRIES.map((e) => [
    e.id,
    {
      label: e.label.toLowerCase(),
      words: e.label.toLowerCase().split(/\s+/).filter(Boolean),
      keywords: e.keywords.map((k) => k.toLowerCase()),
    },
  ]),
);

const SCORE_FLOOR = 15;

function isSubsequence(query: string, text: string): boolean {
  let i = 0;
  for (let j = 0; j < text.length && i < query.length; j += 1) {
    if (text[j] === query[i]) i += 1;
  }
  return i === query.length;
}

/** Score a single entry against a normalised (lowercased, trimmed) query. */
function scoreEntry(query: string, surface: MatchSurface): number {
  if (surface.label === query) return 100;
  if (surface.label.startsWith(query)) return 85;
  if (surface.words.some((w) => w.startsWith(query))) return 70;
  for (const kw of surface.keywords) {
    if (kw === query || kw.startsWith(query)) return 55;
  }
  // Fuzzy subsequence — reward compactness (shorter label => tighter match).
  if (isSubsequence(query, surface.label)) {
    return Math.max(SCORE_FLOOR, 45 - Math.min(25, surface.label.length - query.length));
  }
  if (surface.keywords.some((kw) => isSubsequence(query, kw))) return 20;
  return 0;
}

/**
 * Score a fixed entry list against an already-normalised query. Visible +
 * above-floor entries only, best-first; equal scores keep declaration order
 * (Array.prototype.sort is stable in modern engines).
 */
function scoreEntries(
  entries: ReadonlyArray<CommandEntry>,
  q: string,
  ctx: CommandContext,
): ScoredCommand[] {
  const scored: ScoredCommand[] = [];
  for (const entry of entries) {
    if (!entry.visible(ctx)) continue;
    const surface = SURFACES.get(entry.id);
    if (!surface) continue;
    const score = scoreEntry(q, surface);
    if (score >= SCORE_FLOOR) scored.push({ entry, score });
  }
  return scored.sort((a, b) => b.score - a.score);
}

/**
 * Visible entries matching `query`, best-first. Below-floor matches are
 * dropped; ties break by declaration order (stable sort).
 */
export function scoreCommands(query: string, ctx: CommandContext): ScoredCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return scoreEntries(COMMAND_ENTRIES, q, ctx);
}

/**
 * Same scorer, scoped to the Settings surface only (tab + field entries) — the
 * in-page settings search box (`AccountSettings`) reuses this so the palette and
 * the settings page share ONE index. Lexical by design: the corpus is a few
 * dozen short labels + synonyms, so prefix/keyword/fuzzy matching is instant,
 * offline, and free — no embeddings. Enrich `keywords` in the SSOT to widen
 * recall rather than reaching for semantic search.
 */
export function scoreSettingsCommands(query: string, ctx: CommandContext): ScoredCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return scoreEntries(SETTINGS_ENTRIES, q, ctx);
}

/**
 * Minimum score for an entry to be promoted to the highlighted "Top result".
 * 85 = an exact or label-prefix match (see the scoring table) — fuzzy hits are
 * deliberately NOT auto-actioned, only listed.
 */
export const TOP_SCORE_THRESHOLD = 85;

/**
 * The single best match from an already-scored list, if it is a strong
 * (exact / prefix) hit — the "Top result". Takes the scored list so callers
 * that already scored (e.g. `useCommandRegistry`) don't score twice.
 */
export function pickTopCommand(scored: readonly ScoredCommand[]): CommandEntry | null {
  const best = scored[0];
  return best && best.score >= TOP_SCORE_THRESHOLD ? best.entry : null;
}
